import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  ChatBubbleLeftIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  UserIcon,
  PhoneIcon,
  PhoneXMarkIcon,
  CheckIcon,
  ExclamationCircleIcon,
  FlagIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { trackerAPI } from '../../api/tracker.api';
import { getWorkloadStatistics } from '../../api/workload.api';
import { useMessageTemplates, useDeleteTemplate } from '../../hooks/useMessageTemplates';
import { TemplateCreatorModal } from '../TemplateCreatorModal';
import type { AffaireContact, MessageTemplate } from '../../types/tracker.types';
import { toStorageFormat, extractMentionedUserIds, hasMentions } from './mentionUtils';
import './InlineQuickComposer.styles.css';

// ============================================
// Types
// ============================================
export type ComposerMode = 'note' | 'sms' | 'email';
export type UrgencyLevel = 'low' | 'medium' | 'high';

const URGENCY_OPTIONS: { value: UrgencyLevel; label: string; color: string }[] = [
  { value: 'low', label: 'Basse', color: 'low' },
  { value: 'medium', label: 'Moyenne', color: 'medium' },
  { value: 'high', label: 'Haute', color: 'high' },
];

// System/bot accounts excluded from email recipient lists
const EXCLUDED_EMAIL_RECIPIENTS = new Set([
  'lea.chatelain@chatelainxpert.be',
  'claude@system.chatelainxpert.be',
  'backend@pickitsmartly.com',
  'raymonde@lehub.be',
]);

interface EligibleUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  fm_name: string;
}

interface AffaireDetails {
  reference: string;
  address?: string;
  expert?: string;
  expert_phone?: string;
  date_rv?: string;
  heure_rv?: string;
  montant?: number;
}

interface ReplyToEmail {
  id: string;
  sender_address: string;
  sender_name?: string;
  subject: string;
  full_content?: string;
  received_time?: string;
}

interface InlineQuickComposerProps {
  /** Affaire ID for the case */
  affaireId: number;
  /** Callback when a new activity is created */
  onSuccess: () => void;
  /** Optional: default phone for SMS reply */
  defaultPhoneNumber?: string;
  /** Optional: default mode to start with */
  defaultMode?: ComposerMode;
  /** Optional: affaire details for template substitution */
  affaireDetails?: AffaireDetails;
  /** Optional: pre-select assignee and open in note mode (user ID) */
  defaultAssignedTo?: string;
  /** Optional: reply to an existing email (pre-fills composer in reply mode) */
  replyTo?: ReplyToEmail | null;
}

// ============================================
// Helper Functions
// ============================================
const getInitials = (firstName: string, lastName: string): string => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

/**
 * Format Belgian GSM number - removes non-digits and leading 0
 */
const formatBelgianGSM = (input: string): string => {
  let digits = input.replace(/\D/g, '');
  if (digits.startsWith('0')) {
    digits = digits.substring(1);
  }
  return digits;
};

/**
 * Validate Belgian GSM number (must be 9 digits starting with 4)
 */
const isValidBelgianGSM = (input: string): boolean => {
  const digits = formatBelgianGSM(input);
  return digits.length === 9 && digits.startsWith('4');
};

// ============================================
// Component
// ============================================
export const InlineQuickComposer: React.FC<InlineQuickComposerProps> = ({
  affaireId,
  onSuccess,
  defaultPhoneNumber,
  defaultMode: _defaultMode = 'note',
  affaireDetails,
  defaultAssignedTo,
  replyTo,
}) => {
  // ============================================
  // State
  // ============================================
  const [isExpanded, setIsExpanded] = useState(false);
  const [mode, setMode] = useState<ComposerMode | null>(null); // null = not yet selected

  // Single draft content (shared until mode is selected)
  const [draft, setDraft] = useState('');

  // Alias for backwards compat
  const content = draft;
  const setContent = setDraft;

  // Note mode state
  const [assignedTo, setAssignedTo] = useState('');
  const [urgency, setUrgency] = useState<UrgencyLevel>('medium');
  const [eligibleUsers, setEligibleUsers] = useState<EligibleUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // SMS mode state
  const [contacts, setContacts] = useState<AffaireContact[]>([]);
  const [selectedPhones, setSelectedPhones] = useState<string[]>(
    defaultPhoneNumber ? [defaultPhoneNumber] : []
  );
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [customPhoneInput, setCustomPhoneInput] = useState('');
  const [showCustomPhoneInput, setShowCustomPhoneInput] = useState(false);

  // Email mode state
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [showCcField, setShowCcField] = useState(false);
  const [customEmailInput, setCustomEmailInput] = useState('');
  const [showCustomEmailInput, setShowCustomEmailInput] = useState(false);
  const [showBureauTo, setShowBureauTo] = useState(false);
  const [showBureauCc, setShowBureauCc] = useState(false);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  const [shouldPreselectDemandeur, setShouldPreselectDemandeur] = useState(false);

  // Mention state
  const [mentionMap, setMentionMap] = useState<Map<string, string>>(new Map());
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearchTerm, setMentionSearchTerm] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState<number | null>(null);
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0);
  const [filteredMentionUsers, setFilteredMentionUsers] = useState<EligibleUser[]>([]);

  // Template state
  const [showTemplateCreator, setShowTemplateCreator] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [activeTemplate, setActiveTemplate] = useState<MessageTemplate | null>(null);

  // Fetch templates from API
  const { templates: apiTemplates, refetch: refetchTemplates } = useMessageTemplates();
  const { deleteTemplate, isDeleting } = useDeleteTemplate();

  // Workload stats for showing dossier count per user
  const { data: workloadData } = useQuery({
    queryKey: ['workload-stats'],
    queryFn: getWorkloadStatistics,
    staleTime: 60_000,
  });

  const workloadMap = useMemo(() => {
    const map: Record<string, number> = {};
    if (workloadData?.responsables) {
      for (const r of workloadData.responsables) {
        map[r.id] = r.current_active_affaire_count;
      }
    }
    return map;
  }, [workloadData]);

  // Refs
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const barInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const templateMenuRef = useRef<HTMLDivElement>(null);
  const mentionDropdownRef = useRef<HTMLDivElement>(null);

  // ============================================
  // Data Fetching
  // ============================================

  // Fetch eligible users for Note mode
  useEffect(() => {
    const fetchEligibleUsers = async () => {
      try {
        setIsLoadingUsers(true);
        const response: any = await trackerAPI.getEligibleResponsables();
        if (response.success) {
          setEligibleUsers(response.data as EligibleUser[]);
        }
      } catch (error) {
        console.error('Error fetching eligible users:', error);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    // Fetch when expanded and note mode selected, or when mention dropdown opens
    if (((isExpanded && mode !== null) || showMentionDropdown) && eligibleUsers.length === 0) {
      fetchEligibleUsers();
    }
  }, [isExpanded, mode, eligibleUsers.length, showMentionDropdown]);

  // Fetch contacts for SMS mode or when template menu is opened
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        setIsLoadingContacts(true);
        // includeExpertUser: surface the assigned expert's user-account email as a
        // fallback when the FileMaker EXPERT contact has no (or a blank) email.
        const response = await trackerAPI.getAffaireContacts(affaireId, { includeExpertUser: true });
        if (response.success) {
          // Keep the full contact list (incl. EXPERT) so the email pickers can offer
          // the expert's address. EXPERT contacts are excluded from the SMS picker only.
          setContacts(response.data.contacts);
        }
      } catch (error) {
        console.error('Error fetching contacts:', error);
      } finally {
        setIsLoadingContacts(false);
      }
    };

    // Fetch when expanded, template menu is open, or mode is selected
    const shouldFetch = (isExpanded && mode !== null) || showTemplateMenu;
    if (shouldFetch && contacts.length === 0) {
      fetchContacts();
    }
  }, [isExpanded, mode, showTemplateMenu, affaireId, contacts.length]);

  // Auto-select Demandeur when using a template and contacts are loaded
  useEffect(() => {
    if (shouldPreselectDemandeur && contacts.length > 0 && !isLoadingContacts) {
      const demandeur = contacts.find(c =>
        c.roles.some(role => role.toLowerCase().includes('demandeur'))
      );
      if (demandeur?.tel_mobile) {
        setSelectedPhones([demandeur.tel_mobile]);
      }
      setShouldPreselectDemandeur(false);
    }
  }, [shouldPreselectDemandeur, contacts, isLoadingContacts]);

  // Open in note mode with pre-selected assignee when triggered externally
  useEffect(() => {
    if (defaultAssignedTo) {
      setIsExpanded(true);
      setMode('note');
      setAssignedTo(defaultAssignedTo);
      setError(null);
      setHasAttemptedSubmit(false);
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [defaultAssignedTo]);

  // Open in email reply mode when replyTo is set
  useEffect(() => {
    if (replyTo) {
      setSelectedEmails([replyTo.sender_address]);
      const subj = replyTo.subject || '';
      setEmailSubject(subj.startsWith('Re:') ? subj : `Re: ${subj}`);
      setIsExpanded(true);
      setMode('email');
      setError(null);
      setHasAttemptedSubmit(false);
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [replyTo]);

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set to scrollHeight to fit content
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [draft]);

  // Filter mention users based on search term
  useEffect(() => {
    if (mentionSearchTerm) {
      const term = mentionSearchTerm.toLowerCase();
      const filtered = eligibleUsers.filter(
        (user) =>
          user.first_name?.toLowerCase().startsWith(term) ||
          user.last_name?.toLowerCase().startsWith(term) ||
          user.email?.toLowerCase().startsWith(term)
      );
      setFilteredMentionUsers(filtered);
      setMentionSelectedIndex(0);
    } else {
      setFilteredMentionUsers(eligibleUsers);
      setMentionSelectedIndex(0);
    }
  }, [mentionSearchTerm, eligibleUsers]);

  // Auto-set assignee from mentions
  useEffect(() => {
    if (hasMentions(draft, mentionMap)) {
      const storageText = toStorageFormat(draft, mentionMap);
      const ids = extractMentionedUserIds(storageText);
      if (ids.length > 0 && assignedTo !== ids[0]) {
        setAssignedTo(ids[0]);
      }
    }
  }, [draft, mentionMap]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close mention dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        mentionDropdownRef.current &&
        !mentionDropdownRef.current.contains(e.target as Node) &&
        barInputRef.current &&
        !barInputRef.current.contains(e.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target as Node)
      ) {
        setShowMentionDropdown(false);
      }
    };

    if (showMentionDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMentionDropdown]);

  // ============================================
  // Handlers
  // ============================================

  // Expand the composer with a specific mode
  const handleExpand = useCallback((targetMode: ComposerMode) => {
    setMode(targetMode);
    setIsExpanded(true);
    setError(null);
    setHasAttemptedSubmit(false);

    // Auto-fill email subject with affaire reference
    if (targetMode === 'email' && !emailSubject) {
      const ref = affaireDetails?.reference || String(affaireId);
      const addr = affaireDetails?.address;
      setEmailSubject(addr ? `Expertise ${ref} — ${addr}` : `Expertise ${ref}`);
    }

    // Focus textarea after expansion
    setTimeout(() => {
      textareaRef.current?.focus();
      // Move cursor to end of text
      if (textareaRef.current) {
        const len = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(len, len);
      }
    }, 100);
  }, [emailSubject, affaireDetails, affaireId]);

  // Collapse the composer
  const handleCollapse = useCallback(() => {
    setIsExpanded(false);
    setMode(null);
    setError(null);
    setHasAttemptedSubmit(false);
    // Clear draft and active template on close
    setDraft('');
    setActiveTemplate(null);
    setSelectedPhones(defaultPhoneNumber ? [defaultPhoneNumber] : []);
    setAssignedTo('');
    setUrgency('medium');
    setCustomPhoneInput('');
    setShowCustomPhoneInput(false);
    // Clear email state
    setSelectedEmails([]);
    setCcEmails([]);
    setEmailSubject('');
    setShowCcField(false);
    setCustomEmailInput('');
    setShowCustomEmailInput(false);
    setShowBureauTo(false);
    setShowBureauCc(false);
    // Clear mention state
    setMentionMap(new Map());
    setShowMentionDropdown(false);
    setMentionSearchTerm('');
    setMentionStartIndex(null);
  }, [defaultPhoneNumber]);

  // Detect @mention in text input
  const handleMentionDetection = useCallback((text: string, cursorPosition: number) => {
    const textBeforeCursor = text.slice(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const charBefore = textBeforeCursor[lastAtIndex - 1];
      if (lastAtIndex === 0 || charBefore === ' ' || charBefore === '\n') {
        const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
        if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
          // Check if this matches an existing mention name (already completed)
          const existingMention = Array.from(mentionMap.keys()).find(
            (name) => textAfterAt === name
          );
          if (!existingMention) {
            setMentionStartIndex(lastAtIndex);
            setMentionSearchTerm(textAfterAt);
            setShowMentionDropdown(true);
            return;
          }
        }
      }
    }

    setShowMentionDropdown(false);
    setMentionStartIndex(null);
    setMentionSearchTerm('');
  }, [mentionMap]);

  // Insert a mention from dropdown selection
  const insertMention = useCallback((user: EligibleUser, inputEl: HTMLInputElement | HTMLTextAreaElement | null) => {
    if (mentionStartIndex === null || !inputEl) return;

    const displayName = `${user.first_name} ${user.last_name}`.trim();
    const storageMention = `@[${displayName}](${user.id})`;

    // Update mention map
    setMentionMap((prev) => {
      const newMap = new Map(prev);
      newMap.set(displayName, storageMention);
      return newMap;
    });

    // Insert into draft text
    const beforeMention = draft.slice(0, mentionStartIndex);
    const afterMention = draft.slice(mentionStartIndex + 1 + mentionSearchTerm.length);
    const displayMention = `@${displayName}`;
    const newDraft = beforeMention + displayMention + ' ' + afterMention;

    setDraft(newDraft);
    setShowMentionDropdown(false);
    setMentionStartIndex(null);
    setMentionSearchTerm('');

    // Set cursor after the mention
    setTimeout(() => {
      if (inputEl) {
        const newCursorPos = beforeMention.length + displayMention.length + 1;
        inputEl.selectionStart = newCursorPos;
        inputEl.selectionEnd = newCursorPos;
        inputEl.focus();
      }
    }, 0);
  }, [mentionStartIndex, mentionSearchTerm, draft]);

  // Handle mention keyboard navigation
  const handleMentionKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): boolean => {
    if (!showMentionDropdown || filteredMentionUsers.length === 0) return false;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionSelectedIndex((prev) =>
        prev < filteredMentionUsers.length - 1 ? prev + 1 : 0
      );
      return true;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionSelectedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredMentionUsers.length - 1
      );
      return true;
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(filteredMentionUsers[mentionSelectedIndex], e.currentTarget);
      return true;
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowMentionDropdown(false);
      return true;
    }
    return false;
  }, [showMentionDropdown, filteredMentionUsers, mentionSelectedIndex, insertMention]);

  // Quick send from collapsed bar (when mention detected)
  const handleQuickSend = useCallback(async () => {
    if (!draft.trim()) return;

    const storageContent = toStorageFormat(draft, mentionMap);
    const mentionedIds = extractMentionedUserIds(storageContent);

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await trackerAPI.createTrackerCom({
        affaire_id: affaireId,
        content: storageContent.trim(),
      });

      if (mentionedIds.length > 0) {
        await trackerAPI.createTrackerComMetadata(response.data.id, {
          assigned_to: mentionedIds[0],
          assigned_role: null,
          urgency: 'medium',
          comment: null,
        });
      }

      // Reset
      setDraft('');
      setMentionMap(new Map());
      setAssignedTo('');
      onSuccess();
    } catch (err: any) {
      console.error('Error creating quick note:', err);
      setError(err.response?.data?.message || 'Erreur lors de la création de la note');
    } finally {
      setIsSubmitting(false);
    }
  }, [draft, mentionMap, affaireId, onSuccess]);

  // Handle custom phone input change
  const handleCustomPhoneChange = useCallback((value: string) => {
    // Allow only digits and spaces for user-friendly input
    const cleaned = value.replace(/[^\d\s]/g, '');
    setCustomPhoneInput(cleaned);
  }, []);

  // Add custom phone to selected phones
  const handleAddCustomPhone = useCallback(() => {
    if (isValidBelgianGSM(customPhoneInput)) {
      const digits = formatBelgianGSM(customPhoneInput);
      const formattedNumber = `+32${digits}`;
      if (!selectedPhones.includes(formattedNumber)) {
        setSelectedPhones(prev => [...prev, formattedNumber]);
      }
      setCustomPhoneInput('');
    }
  }, [customPhoneInput, selectedPhones]);

  // Handle phone toggle with auto-update for active template
  const handlePhoneToggle = useCallback((phone: string) => {
    setSelectedPhones(prev => {
      const isSelected = prev.includes(phone);
      const newPhones = isSelected
        ? prev.filter(p => p !== phone)
        : [...prev, phone];

      // Auto-update draft if activeTemplate contains {destinataire}
      if (activeTemplate && activeTemplate.content.includes('{destinataire}')) {
        // Find the contact for the newly selected phone (if selecting, not deselecting)
        if (!isSelected && newPhones.length === 1) {
          const newContact = contacts.find(c => c.tel_mobile === phone);
          if (newContact) {
            const newDestinataireName = `${newContact.prenom || ''} ${newContact.nom_representant || ''}`.trim()
              || activeTemplate.fallback_greeting || 'Madame, Monsieur';

            // Find other contact names for placeholders
            const demandeur = contacts.find(c =>
              c.roles.some(role => role.toLowerCase().includes('demandeur'))
            );
            const demandeurName = demandeur
              ? `${demandeur.prenom || ''} ${demandeur.nom_representant || ''}`.trim()
              : activeTemplate.fallback_greeting || 'Madame, Monsieur';

            const proprietaire = contacts.find(c =>
              c.roles.some(role => role.toLowerCase().includes('propriétaire') || role.toLowerCase().includes('proprietaire'))
            );
            const proprietaireName = proprietaire
              ? `${proprietaire.prenom || ''} ${proprietaire.nom_representant || ''}`.trim()
              : '';

            const client = contacts.find(c =>
              c.roles.some(role => role.toLowerCase().includes('client'))
            );
            const clientName = client
              ? `${client.prenom || ''} ${client.nom_representant || ''}`.trim()
              : '';

            // Format date_rv for display (dd/MM/yyyy)
            const formatDateRv = (dateStr?: string): string => {
              if (!dateStr) return '';
              try {
                const date = new Date(dateStr);
                if (isNaN(date.getTime())) return '';
                return date.toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric' });
              } catch {
                return '';
              }
            };

            // Format montant for display (e.g., 1.234,56 €)
            const formatMontant = (amount?: number): string => {
              if (amount === undefined || amount === null) return '';
              return amount.toLocaleString('fr-BE', { style: 'currency', currency: 'EUR' });
            };

            // Re-substitute all placeholders with new destinataire
            let newContent = activeTemplate.content
              .replace(/{destinataire}/g, newDestinataireName)
              .replace(/{demandeur}/g, demandeurName)
              .replace(/{proprietaire}/g, proprietaireName)
              .replace(/{client}/g, clientName)
              .replace(/{expert}/g, affaireDetails?.expert || '')
              .replace(/{tel_expert}/g, affaireDetails?.expert_phone || '')
              .replace(/{reference}/g, affaireDetails?.reference || String(affaireId))
              .replace(/{adresse}/g, affaireDetails?.address || '')
              .replace(/{date_rv}/g, formatDateRv(affaireDetails?.date_rv))
              .replace(/{heure_rv}/g, affaireDetails?.heure_rv || '')
              .replace(/{montant}/g, formatMontant(affaireDetails?.montant));

            newContent = newContent.replace(/\(\s*\)/g, '').replace(/\s+/g, ' ').trim();
            setDraft(newContent);
          }
        }
      }

      return newPhones;
    });
  }, [activeTemplate, contacts, affaireDetails, affaireId]);

  // Handle submit
  const handleSubmit = async () => {
    // Safety guard - mode must be selected
    if (!mode) return;

    setHasAttemptedSubmit(true);
    setError(null);

    // Validation
    if (!content.trim()) {
      setError(
        mode === 'note' ? 'La note ne peut pas être vide.' :
        mode === 'email' ? "Le contenu de l'email ne peut pas être vide." :
        'Le message ne peut pas être vide.'
      );
      return;
    }

    if (mode === 'sms' && selectedPhones.length === 0) {
      setError('Sélectionnez au moins un destinataire.');
      return;
    }

    if (mode === 'email') {
      if (selectedEmails.length === 0) {
        setError('Sélectionnez au moins un destinataire.');
        return;
      }
      if (!emailSubject.trim()) {
        setError("L'objet est requis.");
        return;
      }
    }

    try {
      setIsSubmitting(true);

      if (mode === 'note') {
        // Convert mentions to storage format before saving
        const storageContent = toStorageFormat(content, mentionMap);
        // Create tracker_com (internal note)
        const response = await trackerAPI.createTrackerCom({
          affaire_id: affaireId,
          content: storageContent.trim(),
        });

        // Add metadata if assignee is set or urgency is not default
        if (assignedTo || urgency !== 'medium') {
          const trackerComId = response.data.id;
          await trackerAPI.createTrackerComMetadata(trackerComId, {
            assigned_to: assignedTo || null,
            assigned_role: null,
            urgency: urgency,
            comment: null,
          });
        }
      } else if (mode === 'email') {
        if (replyTo) {
          // Reply to existing email — uses MS Graph threading.
          // To/CC fully override the reply recipients (user can replace the sender).
          await trackerAPI.replyToEmail(replyTo.id, {
            body: content.trim(),
            to: selectedEmails,
            cc: ccEmails.length > 0 ? ccEmails : undefined,
          });
        } else {
          // Send new email — plain text, backend wraps in branded HTML template
          await trackerAPI.sendTrackerEmail({
            affaire_id: affaireId,
            to: selectedEmails,
            cc: ccEmails.length > 0 ? ccEmails : undefined,
            subject: emailSubject.trim(),
            body: content.trim(),
          });
        }
      } else {
        // Send SMS
        await trackerAPI.sendTrackerSMS({
          affaire_id: affaireId,
          phone_numbers: selectedPhones,
          message: content.trim(),
          urgency: 'medium', // Default, no urgency UI per spec
          assigned_to: null,
        });
      }

      // Success: clear and collapse
      setDraft('');
      setActiveTemplate(null);
      setSelectedPhones(defaultPhoneNumber ? [defaultPhoneNumber] : []);
      setSelectedEmails([]);
      setCcEmails([]);
      setEmailSubject('');
      setShowCcField(false);
      setAssignedTo('');
      setUrgency('medium');
      setIsExpanded(false);
      setMode(null);
      setHasAttemptedSubmit(false);
      setMentionMap(new Map());
      setShowMentionDropdown(false);

      // Notify parent
      onSuccess();
    } catch (error: any) {
      console.error(`Error creating ${mode}:`, error);
      setError(
        error.response?.data?.message || (
          mode === 'note' ? 'Erreur lors de la création de la note' :
          mode === 'email' ? "Erreur lors de l'envoi de l'email" :
          "Erreur lors de l'envoi du SMS"
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showTemplateMenu) {
          setShowTemplateMenu(false);
        } else if (isExpanded) {
          handleCollapse();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded, showTemplateMenu, handleCollapse]);

  // Close template menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (templateMenuRef.current && !templateMenuRef.current.contains(e.target as Node)) {
        setShowTemplateMenu(false);
      }
    };

    if (showTemplateMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTemplateMenu]);

  // Handle template selection with variable substitution
  const handleSelectTemplate = useCallback((template: MessageTemplate) => {
    // Find contact by preselect_role if specified
    let targetContact: AffaireContact | undefined;
    if (template.preselect_role) {
      targetContact = contacts.find(c =>
        c.roles.some(role => role.toLowerCase().includes(template.preselect_role!))
      );
    }

    // Get contact name for {destinataire} substitution
    const destinataireName = targetContact
      ? `${targetContact.prenom || ''} ${targetContact.nom_representant || ''}`.trim()
      : template.fallback_greeting || 'Madame, Monsieur';

    // Find demandeur specifically for {demandeur} placeholder
    const demandeur = contacts.find(c =>
      c.roles.some(role => role.toLowerCase().includes('demandeur'))
    );
    const demandeurName = demandeur
      ? `${demandeur.prenom || ''} ${demandeur.nom_representant || ''}`.trim()
      : template.fallback_greeting || 'Madame, Monsieur';

    // Find proprietaire for {proprietaire} placeholder
    const proprietaire = contacts.find(c =>
      c.roles.some(role => role.toLowerCase().includes('propriétaire') || role.toLowerCase().includes('proprietaire'))
    );
    const proprietaireName = proprietaire
      ? `${proprietaire.prenom || ''} ${proprietaire.nom_representant || ''}`.trim()
      : '';

    // Find client for {client} placeholder
    const client = contacts.find(c =>
      c.roles.some(role => role.toLowerCase().includes('client'))
    );
    const clientName = client
      ? `${client.prenom || ''} ${client.nom_representant || ''}`.trim()
      : '';

    // Format date_rv for display (dd/MM/yyyy)
    const formatDateRv = (dateStr?: string): string => {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric' });
      } catch {
        return '';
      }
    };

    // Format montant for display (e.g., 1.234,56 €)
    const formatMontant = (amount?: number): string => {
      if (amount === undefined || amount === null) return '';
      return amount.toLocaleString('fr-BE', { style: 'currency', currency: 'EUR' });
    };

    // Substitute placeholders
    let content = template.content
      .replace(/{destinataire}/g, destinataireName)
      .replace(/{demandeur}/g, demandeurName)
      .replace(/{proprietaire}/g, proprietaireName)
      .replace(/{client}/g, clientName)
      .replace(/{expert}/g, affaireDetails?.expert || '')
      .replace(/{tel_expert}/g, affaireDetails?.expert_phone || '')
      .replace(/{reference}/g, affaireDetails?.reference || String(affaireId))
      .replace(/{adresse}/g, affaireDetails?.address || '')
      .replace(/{date_rv}/g, formatDateRv(affaireDetails?.date_rv))
      .replace(/{heure_rv}/g, affaireDetails?.heure_rv || '')
      .replace(/{montant}/g, formatMontant(affaireDetails?.montant));

    // Clean up empty placeholders or double spaces
    content = content.replace(/\(\s*\)/g, '').replace(/\s+/g, ' ').trim();

    setDraft(content);
    setShowTemplateMenu(false);
    setActiveTemplate(template);

    // Auto-select recipient based on preselect_role and template type
    if (template.type === 'email') {
      // For email templates, preselect email instead of phone
      if (template.preselect_role && targetContact?.email_1) {
        setSelectedEmails([targetContact.email_1]);
      }
      // Set subject from template (with same variable substitution)
      if (template.subject) {
        let subjectContent = template.subject
          .replace(/{destinataire}/g, destinataireName)
          .replace(/{demandeur}/g, demandeurName)
          .replace(/{proprietaire}/g, proprietaireName)
          .replace(/{client}/g, clientName)
          .replace(/{expert}/g, affaireDetails?.expert || '')
          .replace(/{reference}/g, affaireDetails?.reference || String(affaireId))
          .replace(/{adresse}/g, affaireDetails?.address || '')
          .replace(/{date_rv}/g, formatDateRv(affaireDetails?.date_rv))
          .replace(/{heure_rv}/g, affaireDetails?.heure_rv || '')
          .replace(/{montant}/g, formatMontant(affaireDetails?.montant));
        subjectContent = subjectContent.replace(/\(\s*\)/g, '').replace(/\s+/g, ' ').trim();
        setEmailSubject(subjectContent);
      }
      handleExpand('email');
    } else if (template.type === 'sms') {
      if (template.preselect_role && targetContact?.tel_mobile) {
        setSelectedPhones([targetContact.tel_mobile]);
      } else if (template.preselect_role) {
        setShouldPreselectDemandeur(true);
      }
      handleExpand('sms');
    } else {
      handleExpand('note');
    }
  }, [handleExpand, contacts, affaireDetails, affaireId]);

  // Handle template save from modal
  const handleTemplateSave = useCallback((_template: MessageTemplate) => {
    refetchTemplates();
    setEditingTemplate(null);
  }, [refetchTemplates]);

  // ============================================
  // Computed Values
  // ============================================
  const selectedUser = eligibleUsers.find(u => u.id === assignedTo);
  const draftHasMentions = hasMentions(draft, mentionMap);

  const isSubmitDisabled =
    isSubmitting ||
    !content.trim() ||
    (mode === 'sms' && selectedPhones.length === 0) ||
    (mode === 'email' && (selectedEmails.length === 0 || !emailSubject.trim()));

  // ============================================
  // Render
  // ============================================
  return (
    <div
      ref={containerRef}
      className={`quick-composer ${isExpanded ? 'quick-composer--expanded' : ''}`}
    >
      {/* Header Bar with inline input (only when collapsed) */}
      {!isExpanded && (
        <div className="quick-composer__bar">
          <div className="quick-composer__input-wrapper">
            <input
              ref={barInputRef}
              type="text"
              className="quick-composer__inline-input"
              placeholder="Ajouter une note, @mentionner..."
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                handleMentionDetection(e.target.value, e.target.selectionStart ?? e.target.value.length);
              }}
              onKeyDown={(e) => {
                // Handle mention dropdown navigation first
                if (handleMentionKeyDown(e as React.KeyboardEvent<HTMLInputElement>)) return;
                // Enter key: quick send if mentions, else expand as note
                if (e.key === 'Enter' && draft.trim()) {
                  e.preventDefault();
                  if (draftHasMentions) {
                    handleQuickSend();
                  } else {
                    handleExpand('note');
                  }
                }
              }}
              aria-label="Message rapide"
              disabled={isSubmitting}
            />

            {/* Mention dropdown (collapsed bar) */}
            {showMentionDropdown && !isExpanded && (
              <div ref={mentionDropdownRef} className="quick-composer__mention-dropdown">
                {filteredMentionUsers.length > 0 ? (
                  filteredMentionUsers.slice(0, 8).map((user, index) => (
                    <div
                      key={user.id}
                      className={`quick-composer__mention-item ${index === mentionSelectedIndex ? 'selected' : ''}`}
                      onClick={() => insertMention(user, barInputRef.current)}
                    >
                      <div className="quick-composer__mention-avatar">
                        {getInitials(user.first_name, user.last_name)}
                      </div>
                      <div className="quick-composer__mention-info">
                        <div className="quick-composer__mention-name">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="quick-composer__mention-email">{user.email}</div>
                      </div>
                      <div className={`quick-composer__mention-role ${user.role}`}>{user.role}</div>
                    </div>
                  ))
                ) : (
                  <div className="quick-composer__mention-empty">Aucun utilisateur trouvé</div>
                )}
              </div>
            )}
          </div>

          {/* Mode buttons OR Send button */}
          {draftHasMentions ? (
            <button
              type="button"
              className="quick-composer__send-btn"
              onClick={handleQuickSend}
              disabled={isSubmitting || !draft.trim()}
            >
              {isSubmitting ? 'Envoi...' : (
                <>
                  <PaperAirplaneIcon />
                  <span>Envoyer</span>
                </>
              )}
            </button>
          ) : (
            <div className="quick-composer__modes" role="tablist">
              <button
                type="button"
                className={`quick-composer__mode-btn ${mode === 'note' ? 'active' : ''}`}
                onClick={() => handleExpand('note')}
                role="tab"
                aria-selected={mode === 'note'}
                aria-label="Note interne"
                title="Note interne"
              >
                <ChatBubbleLeftIcon />
                <span className="quick-composer__mode-label">Note</span>
              </button>
              <button
                type="button"
                className={`quick-composer__mode-btn ${mode === 'sms' ? 'active' : ''}`}
                onClick={() => handleExpand('sms')}
                role="tab"
                aria-selected={mode === 'sms'}
                aria-label="SMS"
                title="SMS"
              >
                <DevicePhoneMobileIcon />
                <span className="quick-composer__mode-label">SMS</span>
              </button>
              <button
                type="button"
                className={`quick-composer__mode-btn ${mode === 'email' ? 'active' : ''}`}
                onClick={() => handleExpand('email')}
                role="tab"
                aria-selected={mode === 'email'}
                aria-label="Email"
                title="Email"
              >
                <EnvelopeIcon />
                <span className="quick-composer__mode-label">Email</span>
              </button>

              {/* Templates dropdown */}
              <div className="quick-composer__template-container" ref={templateMenuRef}>
                <button
                  type="button"
                  className={`quick-composer__mode-btn quick-composer__mode-btn--template ${showTemplateMenu ? 'active' : ''}`}
                  onClick={() => setShowTemplateMenu(!showTemplateMenu)}
                  aria-label="Modèles"
                  title="Modèles"
                  aria-expanded={showTemplateMenu}
                  aria-haspopup="menu"
                >
                  <DocumentTextIcon />
                  <span className="quick-composer__mode-label">Modèles</span>
                  <ChevronDownIcon className="quick-composer__chevron" />
                </button>

                {showTemplateMenu && (
                  <div className="quick-composer__template-menu" role="menu">
                    {apiTemplates.length === 0 ? (
                      <div className="quick-composer__template-empty">
                        Aucun modèle disponible
                      </div>
                    ) : (
                      apiTemplates.map((template) => (
                        <div
                          key={template.id}
                          className="quick-composer__template-item"
                          role="menuitem"
                        >
                          <button
                            type="button"
                            className="quick-composer__template-item-main"
                            onClick={() => handleSelectTemplate(template)}
                          >
                            <span className={`quick-composer__template-type quick-composer__template-type--${template.type}`}>
                              {template.type.toUpperCase()}
                            </span>
                            <span className="quick-composer__template-label">{template.name}</span>
                          </button>
                          {!template.is_system && (
                            <div className="quick-composer__template-actions">
                              <button
                                type="button"
                                className="quick-composer__template-action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowTemplateMenu(false);
                                  setEditingTemplate(template);
                                  setShowTemplateCreator(true);
                                }}
                                title="Modifier"
                              >
                                <PencilIcon />
                              </button>
                              <button
                                type="button"
                                className="quick-composer__template-action-btn quick-composer__template-action-btn--danger"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Supprimer le modèle "${template.name}" ?`)) {
                                    await deleteTemplate(template.id);
                                    refetchTemplates();
                                  }
                                }}
                                disabled={isDeleting}
                                title="Supprimer"
                              >
                                <TrashIcon />
                              </button>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                    <div className="quick-composer__template-divider" />
                    <button
                      type="button"
                      className="quick-composer__template-item quick-composer__template-create"
                      onClick={() => {
                        setShowTemplateMenu(false);
                        setEditingTemplate(null);
                        setShowTemplateCreator(true);
                      }}
                      role="menuitem"
                    >
                      <PlusIcon className="quick-composer__template-create-icon" />
                      <span>Créer un modèle</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Expandable Content */}
      <div className="quick-composer__content">
        <div className="quick-composer__content-inner">
          {isExpanded && (
            <>
              {/* Form Grid */}
              <div className={`quick-composer__form ${mode === 'email' ? 'quick-composer__form--email' : ''}`}>

                {/* Email mode: To / CC / Subject ABOVE textarea */}
                {mode === 'email' && (
                  <div className="quick-composer__email-header">
                    <>
                    {/* To / CC pickers — editable for both new emails and replies.
                        In reply mode the To field is pre-filled with the sender but
                        stays fully editable (the user can replace/remove it). */}
                    <div className="quick-composer__email-row">
                      <span className="quick-composer__email-row-label">À</span>
                      <div className="quick-composer__email-row-content">
                        {isLoadingContacts ? (
                          <div className="quick-composer__loading">Chargement...</div>
                        ) : (
                          <div className="quick-composer__recipients quick-composer__recipients--email" role="listbox" aria-label="Destinataires (À)">
                            {/* Affaire contacts */}
                            {contacts.filter(c => Boolean(c.email_1)).map((contact) => {
                              const hasEmail = Boolean(contact.email_1);
                              const isSelected = hasEmail && selectedEmails.includes(contact.email_1!);
                              return (
                                <div
                                  key={`email-to-${contact.k_id_contact}-${contact.nom_representant}`}
                                  className={`quick-composer__recipient ${isSelected ? 'selected' : ''} ${!hasEmail ? 'disabled' : ''}`}
                                  onClick={hasEmail ? () => setSelectedEmails(prev =>
                                    prev.includes(contact.email_1!) ? prev.filter(e => e !== contact.email_1!) : [...prev, contact.email_1!]
                                  ) : undefined}
                                  role="option"
                                  aria-selected={isSelected}
                                  aria-disabled={!hasEmail}
                                  tabIndex={hasEmail ? 0 : -1}
                                  title={hasEmail ? contact.email_1 : "Pas d'adresse email"}
                                >
                                  <div className="quick-composer__recipient-checkbox">
                                    {hasEmail ? <CheckIcon /> : <XMarkIcon />}
                                  </div>
                                  <div className="quick-composer__recipient-info">
                                    <span className="quick-composer__recipient-name">
                                      {contact.prenom} {contact.nom_representant}
                                    </span>
                                    <span className={`quick-composer__recipient-phone ${!hasEmail ? 'no-phone' : ''}`}>
                                      <EnvelopeIcon />
                                      {hasEmail ? contact.email_1 : "Pas d'email"}
                                    </span>
                                    {contact.roles.length > 0 && (
                                      <span className="quick-composer__recipient-role">
                                        {contact.roles.join(' • ')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                            {/* Bureau team toggle */}
                            {eligibleUsers.filter(u => u.email && !EXCLUDED_EMAIL_RECIPIENTS.has(u.email) && !contacts.some(c => c.email_1 === u.email)).length > 0 && (
                              <>
                                <button
                                  type="button"
                                  className={`quick-composer__bureau-toggle ${showBureauTo ? 'open' : ''}`}
                                  onClick={() => setShowBureauTo(prev => !prev)}
                                >
                                  <ChevronDownIcon className="quick-composer__bureau-toggle-icon" />
                                  <span>Bureau ({eligibleUsers.filter(u => u.email && !EXCLUDED_EMAIL_RECIPIENTS.has(u.email) && !contacts.some(c => c.email_1 === u.email)).length})</span>
                                </button>
                                {showBureauTo && eligibleUsers
                                  .filter(u => u.email && !contacts.some(c => c.email_1 === u.email))
                                  .map((user) => {
                                    const isSelected = selectedEmails.includes(user.email);
                                    return (
                                      <div
                                        key={`email-to-team-${user.id}`}
                                        className={`quick-composer__recipient ${isSelected ? 'selected' : ''}`}
                                        onClick={() => setSelectedEmails(prev =>
                                          prev.includes(user.email) ? prev.filter(e => e !== user.email) : [...prev, user.email]
                                        )}
                                        role="option"
                                        aria-selected={isSelected}
                                        tabIndex={0}
                                        title={user.email}
                                      >
                                        <div className="quick-composer__recipient-checkbox"><CheckIcon /></div>
                                        <div className="quick-composer__recipient-info">
                                          <span className="quick-composer__recipient-name">
                                            {user.first_name} {user.last_name}
                                          </span>
                                          <span className="quick-composer__recipient-phone">
                                            <EnvelopeIcon />{user.email}
                                          </span>
                                          <span className="quick-composer__recipient-role">BUREAU</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                              </>
                            )}
                            {/* Custom emails added by user */}
                            {selectedEmails
                              .filter(email => !contacts.some(c => c.email_1 === email) && !eligibleUsers.some(u => u.email === email))
                              .map(email => (
                                <div key={email} className="quick-composer__recipient selected custom">
                                  <div className="quick-composer__recipient-checkbox"><CheckIcon /></div>
                                  <div className="quick-composer__recipient-info">
                                    <span className="quick-composer__recipient-name">Email personnalisé</span>
                                    <span className="quick-composer__recipient-phone"><EnvelopeIcon />{email}</span>
                                  </div>
                                  <button type="button" className="quick-composer__recipient-remove"
                                    onClick={(e) => { e.stopPropagation(); setSelectedEmails(prev => prev.filter(e2 => e2 !== email)); }}
                                    title="Supprimer"><XMarkIcon /></button>
                                </div>
                              ))}
                            {/* Custom email toggle */}
                            <button
                              type="button"
                              className={`quick-composer__bureau-toggle ${showCustomEmailInput ? 'open' : ''}`}
                              onClick={() => setShowCustomEmailInput(prev => !prev)}
                            >
                              <ChevronDownIcon className="quick-composer__bureau-toggle-icon" />
                              <span>Autre email</span>
                            </button>
                            {showCustomEmailInput && (
                              <div className="quick-composer__custom-phone">
                                <input
                                  type="email"
                                  className="quick-composer__custom-phone-input quick-composer__custom-email-input"
                                  placeholder="nom@exemple.com"
                                  value={customEmailInput}
                                  onChange={(e) => setCustomEmailInput(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customEmailInput)) {
                                        setSelectedEmails(prev => [...prev, customEmailInput.trim()]);
                                        setCustomEmailInput('');
                                        setShowCustomEmailInput(false);
                                      }
                                    } else if (e.key === 'Escape') {
                                      setShowCustomEmailInput(false);
                                      setCustomEmailInput('');
                                    }
                                  }}
                                  autoFocus
                                />
                                <button type="button" className="quick-composer__custom-phone-add"
                                  onClick={() => {
                                    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customEmailInput)) {
                                      setSelectedEmails(prev => [...prev, customEmailInput.trim()]);
                                      setCustomEmailInput('');
                                      setShowCustomEmailInput(false);
                                    }
                                  }}
                                  disabled={!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customEmailInput)}
                                  title="Ajouter cet email"><PlusIcon /></button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* CC */}
                    <div className="quick-composer__email-row">
                      <span className="quick-composer__email-row-label">CC</span>
                      <div className="quick-composer__email-row-content">
                        {!showCcField ? (
                          <button type="button" className="quick-composer__cc-toggle" onClick={() => setShowCcField(true)}>
                            + Ajouter des destinataires en copie
                          </button>
                        ) : (
                          <div className="quick-composer__recipients quick-composer__recipients--email quick-composer__recipients--cc" role="listbox" aria-label="Destinataires (CC)">
                            {contacts.filter(c => Boolean(c.email_1)).map((contact) => {
                              const hasEmail = Boolean(contact.email_1);
                              const isInTo = hasEmail && selectedEmails.includes(contact.email_1!);
                              const isSelected = hasEmail && ccEmails.includes(contact.email_1!);
                              if (isInTo) return null;
                              return (
                                <div
                                  key={`email-cc-${contact.k_id_contact}-${contact.nom_representant}`}
                                  className={`quick-composer__recipient ${isSelected ? 'selected' : ''} ${!hasEmail ? 'disabled' : ''}`}
                                  onClick={hasEmail ? () => setCcEmails(prev =>
                                    prev.includes(contact.email_1!) ? prev.filter(e => e !== contact.email_1!) : [...prev, contact.email_1!]
                                  ) : undefined}
                                  role="option" aria-selected={isSelected} aria-disabled={!hasEmail}
                                >
                                  <div className="quick-composer__recipient-checkbox">{hasEmail ? <CheckIcon /> : <XMarkIcon />}</div>
                                  <div className="quick-composer__recipient-info">
                                    <span className="quick-composer__recipient-name">{contact.prenom} {contact.nom_representant}</span>
                                    <span className={`quick-composer__recipient-phone ${!hasEmail ? 'no-phone' : ''}`}>
                                      <EnvelopeIcon />{hasEmail ? contact.email_1 : "Pas d'email"}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                            {/* Bureau team toggle for CC */}
                            {eligibleUsers.filter(u => u.email && !EXCLUDED_EMAIL_RECIPIENTS.has(u.email) && !selectedEmails.includes(u.email) && !contacts.some(c => c.email_1 === u.email)).length > 0 && (
                              <>
                                <button
                                  type="button"
                                  className={`quick-composer__bureau-toggle ${showBureauCc ? 'open' : ''}`}
                                  onClick={() => setShowBureauCc(prev => !prev)}
                                >
                                  <ChevronDownIcon className="quick-composer__bureau-toggle-icon" />
                                  <span>Bureau</span>
                                </button>
                                {showBureauCc && eligibleUsers
                                  .filter(u => u.email && !selectedEmails.includes(u.email) && !contacts.some(c => c.email_1 === u.email))
                                  .map((user) => {
                                    const isSelected = ccEmails.includes(user.email);
                                    return (
                                      <div key={`email-cc-team-${user.id}`}
                                        className={`quick-composer__recipient ${isSelected ? 'selected' : ''}`}
                                        onClick={() => setCcEmails(prev =>
                                          prev.includes(user.email) ? prev.filter(e => e !== user.email) : [...prev, user.email]
                                        )}
                                        role="option" aria-selected={isSelected}
                                      >
                                        <div className="quick-composer__recipient-checkbox"><CheckIcon /></div>
                                        <div className="quick-composer__recipient-info">
                                          <span className="quick-composer__recipient-name">{user.first_name} {user.last_name}</span>
                                          <span className="quick-composer__recipient-phone"><EnvelopeIcon />{user.email}</span>
                                          <span className="quick-composer__recipient-role">BUREAU</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Subject — read-only on a reply (keeps the "Re:" thread subject),
                        editable when composing a new email. */}
                    <div className="quick-composer__email-row">
                      <span className="quick-composer__email-row-label">Objet</span>
                      <div className="quick-composer__email-row-content">
                        {replyTo ? (
                          <div className="quick-composer__reply-recipient">{emailSubject}</div>
                        ) : (
                          <input
                            type="text"
                            className={`quick-composer__email-subject-input ${hasAttemptedSubmit && !emailSubject.trim() ? 'error' : ''}`}
                            placeholder="Objet de l'email..."
                            value={emailSubject}
                            onChange={(e) => setEmailSubject(e.target.value)}
                            disabled={isSubmitting}
                          />
                        )}
                      </div>
                    </div>
                    </>
                  </div>
                )}

                {/* Textarea */}
                <div className="quick-composer__textarea-wrapper">
                  <textarea
                    ref={textareaRef}
                    className={`quick-composer__textarea ${mode === 'email' ? 'quick-composer__textarea--email' : ''} ${hasAttemptedSubmit && !content.trim() ? 'error' : ''}`}
                    placeholder={
                      mode === 'note' ? 'Écrire une note interne... (@mention pour taguer)' :
                      mode === 'email' ? "Écrire le contenu de l'email..." :
                      'Écrire un SMS...'
                    }
                    value={content}
                    onChange={(e) => {
                      setContent(e.target.value);
                      if (mode === 'note') {
                        handleMentionDetection(e.target.value, e.target.selectionStart ?? e.target.value.length);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (mode === 'note') {
                        handleMentionKeyDown(e as React.KeyboardEvent<HTMLTextAreaElement>);
                      }
                    }}
                    disabled={isSubmitting}
                    aria-label={mode === 'note' ? 'Contenu de la note' : mode === 'email' ? "Contenu de l'email" : 'Contenu du SMS'}
                  />

                  {/* Signature notice for email */}
                  {mode === 'email' && (
                    <div className="quick-composer__email-signature-notice">
                      Votre signature sera ajoutée automatiquement
                    </div>
                  )}

                  {/* Quoted original email for reply mode */}
                  {mode === 'email' && replyTo && (
                    <div className="quick-composer__quoted-email">
                      <div className="quick-composer__quoted-email-header">
                        <strong>De:</strong> {replyTo.sender_name ? `${replyTo.sender_name} <${replyTo.sender_address}>` : replyTo.sender_address}
                        {replyTo.received_time && (
                          <span className="quick-composer__quoted-email-date">
                            {' — '}{new Date(replyTo.received_time).toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                      <div className="quick-composer__quoted-email-body">
                        {replyTo.full_content || '(contenu non disponible)'}
                      </div>
                    </div>
                  )}

                  {/* Mention dropdown (expanded textarea) */}
                  {showMentionDropdown && isExpanded && mode === 'note' && (
                    <div ref={mentionDropdownRef} className="quick-composer__mention-dropdown">
                      {filteredMentionUsers.length > 0 ? (
                        filteredMentionUsers.slice(0, 8).map((user, index) => (
                          <div
                            key={user.id}
                            className={`quick-composer__mention-item ${index === mentionSelectedIndex ? 'selected' : ''}`}
                            onClick={() => insertMention(user, textareaRef.current)}
                          >
                            <div className="quick-composer__mention-avatar">
                              {getInitials(user.first_name, user.last_name)}
                            </div>
                            <div className="quick-composer__mention-info">
                              <div className="quick-composer__mention-name">
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="quick-composer__mention-email">{user.email}</div>
                            </div>
                            <div className={`quick-composer__mention-role ${user.role}`}>{user.role}</div>
                          </div>
                        ))
                      ) : (
                        <div className="quick-composer__mention-empty">Aucun utilisateur trouvé</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Options Row: Meta + Actions inline */}
                <div className="quick-composer__options-row">
                  {/* Meta Panel */}
                  <div className="quick-composer__meta">
                    {mode === 'note' ? (
                      /* Note Mode: Assignee Dropdown + Priority */
                      <div className="quick-composer__field quick-composer__field--inline">
                        {isLoadingUsers ? (
                          <div className="quick-composer__loading">
                            Chargement...
                          </div>
                        ) : (
                          <>
                            <div className="quick-composer__assignee-wrapper">
                              <div className={`quick-composer__assignee-avatar ${!selectedUser ? 'empty' : ''}`}>
                                {selectedUser ? (
                                  getInitials(selectedUser.first_name, selectedUser.last_name)
                                ) : (
                                  <UserIcon />
                                )}
                              </div>
                              <select
                                id="composer-assignee"
                                className="quick-composer__select"
                                value={assignedTo}
                                onChange={(e) => setAssignedTo(e.target.value)}
                                disabled={isSubmitting}
                              >
                                <option value="">Non attribué</option>
                                {eligibleUsers.map((user) => {
                                  const count = workloadMap[user.id];
                                  const countLabel = count !== undefined ? ` — ${count} dossier${count !== 1 ? 's' : ''}` : '';
                                  return (
                                    <option key={user.id} value={user.id}>
                                      {user.first_name} {user.last_name}{countLabel}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                            <div className="quick-composer__priority" role="radiogroup" aria-label="Priorité">
                              <FlagIcon className="quick-composer__priority-icon" />
                              {URGENCY_OPTIONS.map((option) => (
                                <button
                                  key={option.value}
                                  type="button"
                                  className={`quick-composer__priority-btn quick-composer__priority-btn--${option.color} ${urgency === option.value ? 'active' : ''}`}
                                  onClick={() => setUrgency(option.value)}
                                  disabled={isSubmitting}
                                  role="radio"
                                  aria-checked={urgency === option.value}
                                  aria-label={`Priorité ${option.label}`}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    ) : mode !== 'email' ? (
                      /* SMS Mode: Recipients List */
                      <div className="quick-composer__field">
                        {isLoadingContacts ? (
                          <div className="quick-composer__loading">
                            Chargement des contacts...
                          </div>
                        ) : contacts.length === 0 ? (
                          <div className="quick-composer__no-contacts">
                            Aucun contact avec numéro de téléphone
                          </div>
                        ) : (
                          <div
                            className="quick-composer__recipients"
                            role="listbox"
                            aria-label="Sélectionner les destinataires"
                            aria-multiselectable="true"
                          >
                            {contacts
                              .filter(c => !c.roles.some(role => role.toUpperCase() === 'EXPERT'))
                              .map((contact) => {
                              const hasPhone = Boolean(contact.tel_mobile);
                              const isSelected = hasPhone && selectedPhones.includes(contact.tel_mobile!);
                              return (
                                <div
                                  key={`${contact.k_id_contact}-${contact.nom_representant}-${contact.tel_mobile || ''}`}
                                  className={`quick-composer__recipient ${isSelected ? 'selected' : ''} ${!hasPhone ? 'disabled' : ''}`}
                                  onClick={hasPhone ? () => handlePhoneToggle(contact.tel_mobile!) : undefined}
                                  role="option"
                                  aria-selected={isSelected}
                                  aria-disabled={!hasPhone}
                                  tabIndex={hasPhone ? 0 : -1}
                                  onKeyDown={hasPhone ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handlePhoneToggle(contact.tel_mobile!);
                                    }
                                  } : undefined}
                                  title={!hasPhone ? 'Pas de numéro de téléphone' : undefined}
                                >
                                  <div className="quick-composer__recipient-checkbox">
                                    {hasPhone ? <CheckIcon /> : <PhoneXMarkIcon />}
                                  </div>
                                  <div className="quick-composer__recipient-info">
                                    <span className="quick-composer__recipient-name">
                                      {contact.prenom} {contact.nom_representant}
                                    </span>
                                    <span className={`quick-composer__recipient-phone ${!hasPhone ? 'no-phone' : ''}`}>
                                      {hasPhone ? <PhoneIcon /> : <PhoneXMarkIcon />}
                                      {hasPhone ? contact.tel_mobile : 'Pas de téléphone'}
                                    </span>
                                    {contact.roles.length > 0 && (
                                      <span className="quick-composer__recipient-role">
                                        {contact.roles.join(' • ')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                            {/* Custom phone numbers added by user */}
                            {selectedPhones
                              .filter(phone => !contacts.some(c => c.tel_mobile === phone))
                              .map(phone => (
                                <div key={phone} className="quick-composer__recipient selected custom">
                                  <div className="quick-composer__recipient-checkbox">
                                    <CheckIcon />
                                  </div>
                                  <div className="quick-composer__recipient-info">
                                    <span className="quick-composer__recipient-name">Numéro personnalisé</span>
                                    <span className="quick-composer__recipient-phone">
                                      <PhoneIcon />
                                      {phone}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    className="quick-composer__recipient-remove"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedPhones(prev => prev.filter(p => p !== phone));
                                    }}
                                    title="Supprimer"
                                  >
                                    <XMarkIcon />
                                  </button>
                                </div>
                              ))}
                            {/* Custom phone number toggle/input - inside recipients */}
                            {showCustomPhoneInput ? (
                              <div className="quick-composer__custom-phone">
                                <span className="quick-composer__custom-phone-prefix">+32</span>
                                <input
                                  type="tel"
                                  className="quick-composer__custom-phone-input"
                                  placeholder="4XX XX XX XX"
                                  value={customPhoneInput}
                                  onChange={(e) => handleCustomPhoneChange(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleAddCustomPhone();
                                    } else if (e.key === 'Escape') {
                                      setShowCustomPhoneInput(false);
                                      setCustomPhoneInput('');
                                    }
                                  }}
                                  autoFocus
                                />
                                <button
                                  type="button"
                                  className="quick-composer__custom-phone-add"
                                  onClick={handleAddCustomPhone}
                                  disabled={!isValidBelgianGSM(customPhoneInput)}
                                  title="Ajouter ce numéro"
                                >
                                  <PlusIcon />
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="quick-composer__add-custom-btn"
                                onClick={() => setShowCustomPhoneInput(true)}
                              >
                                <PlusIcon />
                                <span>Autre</span>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>

                  {/* Action Buttons */}
                  <div className="quick-composer__actions">
                    <button
                      type="button"
                      className="quick-composer__btn quick-composer__btn--secondary"
                      onClick={handleCollapse}
                      disabled={isSubmitting}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      className={`quick-composer__btn ${
                        mode === 'sms' ? 'quick-composer__btn--success' :
                        mode === 'email' ? 'quick-composer__btn--email' :
                        'quick-composer__btn--primary'
                      }`}
                      onClick={handleSubmit}
                      disabled={isSubmitDisabled}
                    >
                      {isSubmitting
                        ? 'Envoi...'
                        : mode === 'note'
                          ? 'Créer la note'
                          : mode === 'email'
                            ? (replyTo ? 'Répondre' : "Envoyer l'email")
                            : selectedPhones.length > 1
                              ? `Envoyer ${selectedPhones.length} SMS`
                              : 'Envoyer SMS'}
                    </button>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="quick-composer__error" role="alert">
                    <ExclamationCircleIcon />
                    <span>{error}</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Template Creator Modal */}
      <TemplateCreatorModal
        isOpen={showTemplateCreator}
        onClose={() => {
          setShowTemplateCreator(false);
          setEditingTemplate(null);
        }}
        onSave={handleTemplateSave}
        editTemplate={editingTemplate}
      />
    </div>
  );
};

export default InlineQuickComposer;
